import { MathBase } from "@goodgamestudios/core";
import { classImplementsInterfaces } from "@goodgamestudios/createjs-utilities";

import { Bezier2D } from "../../../helper/motion/guides/Bezier2D";
import { TweenMaxGroupHelper } from "../../../helper/TweenMaxGroupHelper";

import { AbstractAnimationVO } from "./animationvo/AbstractAnimationVO";
import { GetXPAnimationVO } from "./animationvo/GetXPAnimationVO";

import { IAniHelper } from "./IAniHelper";

import { Circ, Power2, Power3, Power4, Sine } from "gsap";
import { TweenMaxGroup } from "../../../helper/TweenMaxGroup";

import MovieClip = createjs.MovieClip;
import Point = createjs.Point;

export class GetXPAniHelper implements IAniHelper {
    /** Max duration for the chips' slide (for the largest distance) **/
    private static SLIDE_DURATION_MAX: number = 2.0;

    /** Min duration for the chips' slide (for the shortest distance) **/
    private static SLIDE_DURATION_MIN: number = 0.5;

    private ASSUMED_MAX_DISTANCE: number = 714;
    private MOVE_UP_Y_OFFSET: number = 45;
    private _iconXP: Library.PokerTable.OnTableIconXP;
    private _distance: number = NaN;
    private _p_start: Point;
    private _p_target: Point;
    private _dx: number = NaN;
    private _dy: number = NaN;
    private _targetMc: MovieClip;
    private _xpTextMc: MovieClip;
    private _menuPanel: MovieClip;
    private _scale: number = NaN;
    private _startMc: MovieClip;
    private _aniVO: GetXPAnimationVO;

    private _twg_tilt: TweenMaxGroup;
    private _twg_tilt2: TweenMaxGroup;
    private _twg_moveToMenuPanel: TweenMaxGroup;
    private _twg_moveUpIcon: TweenMaxGroup;
    private _twg_scaleDownSize: TweenMaxGroup;
    private _twg_textTween: TweenMaxGroup;

    constructor(scale: number) {
        this._scale = scale;
    }

    public doAnimation(animationVO: AbstractAnimationVO): void {
        this._aniVO = animationVO as GetXPAnimationVO;

        // store needed disps
        this._menuPanel = this._aniVO.menuPanel as MovieClip;

        this._startMc = this._aniVO.playerContainer.mc_bg.mc_xp;
        this._targetMc = this._menuPanel.mc_xp.txt_userlevel;

        // start mc
        this._p_start = new Point(this._startMc.x, this._startMc.y);
        this._p_start = this._startMc.parent.localToGlobal(this._p_start);

        // target mc
        this._p_target = new Point(this._targetMc.x, this._targetMc.y);
        this._p_target = this._targetMc.parent.localToGlobal(this._p_target);

        // xp icon
        this._iconXP = new Library.PokerTable.OnTableIconXP();
        this._iconXP.changeTextFieldCacheScale(2);
        this._iconXP.name = this._aniVO.animationContainer.name + "_icon";
        this._iconXP.scaleX = this._iconXP.scaleY = this._scale;
        this._aniVO.animationContainer.addChild(this._iconXP);
        this._iconXP.mc_icon.width = this._iconXP.mc_icon.height = 0.0;
        this._iconXP.x = this._p_start.x;
        this._iconXP.y = this._p_start.y;

        // text
        this._xpTextMc = this._iconXP.mc_text;
        this._xpTextMc.tf_xp.wordWrap = false;
        this._xpTextMc.tf_xp.multiline = false;
        this._xpTextMc.tf_xp.text = "+ " + this._aniVO.xpAmount;
        this._xpTextMc.alpha = 0;
        // if xp amount is zero, hide it completely
        if (this._aniVO.xpAmount == "0") {
            this._xpTextMc.visible = false;
        }

        // get distance between the seat and the xp bar
        this._distance = MathBase.distance(this._p_start.x, this._p_target.x, this._p_start.y, this._p_target.y);

        // calculate the control point for the bezier curve
        this._dx = this._p_start.x - (this._p_start.x - this._p_target.x) * .5;
        this._dy = this._p_start.y - this._p_target.y;

        // adjust max distance with the current scale (needed e.g. to calculate the desired tween duration of the bezier tween)
        this.ASSUMED_MAX_DISTANCE *= this._scale;

        // draw curve (for visalizing the motion path)
        //			_animationContainer.graphics.lineStyle(2,0xff0000,0.3);
        //			_animationContainer.graphics.moveTo(_p_start.x,_p_start.y);
        //			_animationContainer.graphics.curveTo( _dx, _dy, _p_target.x, _p_target.y);

        // move up
        var icon_upPosY: number = this._iconXP.y - this.MOVE_UP_Y_OFFSET;
        var tf_upPosY: number = this._xpTextMc.y - this.MOVE_UP_Y_OFFSET;
        this._twg_moveUpIcon = new TweenMaxGroup();
        this._twg_moveUpIcon.to(this._iconXP, 0.4, { y: icon_upPosY, ease: Circ.easeOut, onComplete: this.bindFunction(this.onSpawnXPIconComplete), onCompleteParams: [this._twg_moveUpIcon] });
        this._twg_moveUpIcon.play();

        // adjust width of the xp icon to the width/height of the playerContainer's xp icon
        this._twg_tilt = new TweenMaxGroup();
        this._twg_tilt.to(this._iconXP.mc_icon, 0.3, { width: this._startMc.width, height: this._startMc.height, ease: Sine.easeOut });
        this._twg_tilt.play();

        // show xp text amount
        this._twg_textTween = new TweenMaxGroup();
        this._twg_textTween.to(this._xpTextMc, 0.2, { alpha: 1, ease: Power4.easeOut, delay: 0.2 });
        this._twg_textTween.play();
    }

    private onSpawnXPIconComplete(tween: TweenMaxGroup): void {
        var iconScaleX: number = this._iconXP.mc_icon.scaleX;
        // tilt xp icon once
        this._twg_tilt2 = new TweenMaxGroup();
        this._twg_tilt2.to(this._iconXP.mc_icon, 0.25, { scaleX: -iconScaleX, ease: Sine.easeOut });
        this._twg_tilt2.to(this._iconXP.mc_icon, 0.25, { scaleX: iconScaleX, ease: Sine.easeOut, onComplete: this.bindFunction(this.moveIconOnPathToTarget), onCompleteParams: [this._twg_tilt2], delay: 0.25 });
        this._twg_tilt2.play();
    }

    private moveIconOnPathToTarget(tween: any): void {
        this._iconXP.cacheAsBitmap = true;

        // move icon along curve to menu panel
        var moveDuration: number = this.calculateTweenDurationToDistance(this._distance);
        this._twg_moveToMenuPanel = new TweenMaxGroup();
        var bezier: Bezier2D = new Bezier2D(this._iconXP, false, false, false);
        bezier.push(new Point(this._p_start.x, this._p_start.y - this.MOVE_UP_Y_OFFSET));
        bezier.push(new Point(this._dx, this._dy));
        bezier.push(new Point(this._p_target.x, this._p_target.y));
        this._twg_moveToMenuPanel.to(bezier, moveDuration, { position: 1, ease: Power2.easeInOut, onComplete: this.bindFunction(TweenMaxGroupHelper.disposeTweenMaxGroupInstance), onCompleteParams: [this._twg_moveToMenuPanel] });
        this._twg_moveToMenuPanel.play();

        // scale icon to zero size
        this._twg_scaleDownSize = new TweenMaxGroup();
        this._twg_scaleDownSize.to(this._iconXP.mc_icon, 0.2, { scaleX: 1.3, scaleY: 1.3, ease: Circ.easeOut, delay: moveDuration });
        this._twg_scaleDownSize.to(this._iconXP.mc_icon, 0.6, { scaleX: 0, scaleY: 0, ease: Circ.easeOut, delay: 0.2 + moveDuration });
        this._twg_scaleDownSize.play();

        // move up xp amount text some pixels
        this._twg_textTween = new TweenMaxGroup();
        this._twg_textTween.to(this._xpTextMc, 0.2, { y: "-24", ease: Power3.easeOut, delay: 0.3 + moveDuration });
        this._twg_textTween.to(this._xpTextMc, 0.2, { scaleX: 0, scaleY: 0, ease: Power3.easeOut, delay: 0.65 + moveDuration, onComplete: this.bindFunction(this.onTimerComplete), onCompleteParams: [this._twg_textTween] });
        this._twg_textTween.play();
    }

    private onTimerComplete(tween: TweenMaxGroup): void {
        this.onScaleDownComplete();
    }

    private onScaleDownComplete(): void {
        this._aniVO.animationCompleteCallback(this);
    }

    public kill(): void {
        // dispose tweens
        TweenMaxGroupHelper.disposeTweenMaxGroupInstance(this._twg_tilt);
        TweenMaxGroupHelper.disposeTweenMaxGroupInstance(this._twg_tilt2);
        TweenMaxGroupHelper.disposeTweenMaxGroupInstance(this._twg_moveToMenuPanel);
        TweenMaxGroupHelper.disposeTweenMaxGroupInstance(this._twg_moveUpIcon);
        TweenMaxGroupHelper.disposeTweenMaxGroupInstance(this._twg_textTween);
        TweenMaxGroupHelper.disposeTweenMaxGroupInstance(this._twg_scaleDownSize);

        // dispose animation ui component
        this._aniVO.animationContainer.removeChild(this._iconXP);
        if (this._aniVO.animationContainer.parent) {
            this._aniVO.animationContainer.parent.removeChild(this._aniVO.animationContainer);
        }

        // dispose object
        this._iconXP = null;
        this._p_start = null;
        this._p_target = null;

        // dispose vo
        this._aniVO.animationContainer = null;
        this._aniVO.menuPanel = null;
        this._aniVO.playerContainer = null;
        this._aniVO.xpAmount = null;
        this._aniVO = null;
    }

    private calculateTweenDurationToDistance(distance: number): number {
        var p: number = distance / this.ASSUMED_MAX_DISTANCE;

        // calculate the slide ani duration according the to distance between the two seats (wider distances will take longer)
        var slideDuration: number = GetXPAniHelper.SLIDE_DURATION_MAX * p;
        // take at least the mininum slide duration
        return (slideDuration < GetXPAniHelper.SLIDE_DURATION_MIN) ? GetXPAniHelper.SLIDE_DURATION_MIN : slideDuration;
    }
}
classImplementsInterfaces(GetXPAniHelper, "IAniHelper");
